import type {
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import type {
  User,
} from "~/types/auth";

let userPool: CognitoUserPool | null = null;
let pendingMfaUser: CognitoUser | null = null;
let pendingMfaType: "SOFTWARE_TOKEN_MFA" | "SMS_MFA" = "SOFTWARE_TOKEN_MFA";

function isStalePoolError(err: unknown): boolean {
  if (!(err instanceof Error)) {
    return false;
  }
  const msg = err.message || "";
  return msg.includes("Unable to find user pool client") || msg.includes("does not exist");
}

function clearCognitoLocalStorage(): void {
  if (typeof window === "undefined") {
    return;
  }
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith("CognitoIdentityServiceProvider.")) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
  userPool = null;
}

function getUserPool(): CognitoUserPool {
  if (userPool) {
    return userPool;
  }

  const config = useRuntimeConfig();
  const poolId = config.public.cognitoUserPoolId as string;
  const clientId = config.public.cognitoClientId as string;
  const endpoint = (config.public.cognitoEndpoint as string | undefined)?.trim();

  if (!poolId || !clientId) {
    throw new Error("Cognito configuration missing (NUXT_PUBLIC_COGNITO_USER_POOL_ID, NUXT_PUBLIC_COGNITO_CLIENT_ID)");
  }

  const userPoolConfig: {
    UserPoolId: string;
    ClientId: string;
    endpoint?: string;
  } = {
    UserPoolId: poolId,
    ClientId: clientId,
  };

  if (endpoint) {
    userPoolConfig.endpoint = endpoint.replace(/\/$/, "");
  }

  userPool = new CognitoUserPool(userPoolConfig);

  return userPool;
}

function parseJwtPayload(token: string): Record<string, unknown> {
  const base64Url = token.split(".")[1] ?? "";
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const json = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join(""),
  );
  return JSON.parse(json);
}

function resolveDisplayName(payload: Record<string, unknown>): string {
  const firstName = typeof payload.given_name === "string" ? payload.given_name.trim() : "";
  const lastName = typeof payload.family_name === "string" ? payload.family_name.trim() : "";
  const fullName = formatUserName(firstName, lastName);

  if (fullName) {
    return fullName;
  }

  const name = typeof payload.name === "string" ? payload.name.trim() : "";
  if (name) {
    return name;
  }

  const username = typeof payload["cognito:username"] === "string" ? payload["cognito:username"].trim() : "";
  if (username) {
    return username;
  }

  const email = typeof payload.email === "string" ? payload.email.trim() : "";
  return email;
}

function sessionToUser(session: CognitoUserSession): User {
  const idToken = session.getIdToken().getJwtToken();
  const payload = parseJwtPayload(idToken);

  const groups = (payload["cognito:groups"] as string[]) ?? [];
  const customRole = (payload["custom:role"] as string) ?? "";
  let tenant = (payload["custom:tenant"] as string) ?? "";

  if (!tenant) {
    tenant = groups.find((group) => {
      return group.startsWith("tenant:");
    }) ?? "";
  }

  const roles = [...groups];
  if (customRole && !roles.includes(customRole)) {
    roles.push(customRole);
  }

  return {
    sub: (payload.sub as string) ?? "",
    username: (payload["cognito:username"] as string) ?? (payload.email as string) ?? "",
    email: (payload.email as string) ?? "",
    name: resolveDisplayName(payload),
    roles,
    tenant,
  };
}

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
}

export const useCognito = () => {
  /**
   * Get current session if one exists (tokens stored in localStorage by cognito SDK)
   */
  const getSession = (): Promise<{session: CognitoUserSession; user: User; tokens: CognitoTokens} | null> => {
    return new Promise((resolve) => {
      const pool = getUserPool();
      const cognitoUser = pool.getCurrentUser();

      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          if (isStalePoolError(err)) {
            clearCognitoLocalStorage();
          }
          resolve(null);
          return;
        }

        resolve({
          session,
          user: sessionToUser(session),
          tokens: {
            idToken: session.getIdToken().getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
          },
        });
      });
    });
  };

  /**
   * Sign in with email + password
   */
  const signIn = (email: string, password: string): Promise<{user: User; tokens: CognitoTokens}> => {
    return new Promise((resolve, reject) => {
      const pool = getUserPool();
      const cognitoUser = new CognitoUser({Username: email, Pool: pool});
      cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");
      const authDetails = new AuthenticationDetails({Username: email, Password: password});

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session: CognitoUserSession) => {
          resolve({
            user: sessionToUser(session),
            tokens: {
              idToken: session.getIdToken().getJwtToken(),
              accessToken: session.getAccessToken().getJwtToken(),
            },
          });
        },
        onFailure: (err: Error) => {
          if (isStalePoolError(err)) {
            clearCognitoLocalStorage();
          }
          reject(err);
        },
        newPasswordRequired: (_userAttributes: Record<string, string>) => {
          // User needs to set a new password (first login after admin-created account)
          reject(new Error("NEW_PASSWORD_REQUIRED"));
        },
        totpRequired: () => {
          pendingMfaUser = cognitoUser;
          pendingMfaType = "SOFTWARE_TOKEN_MFA";
          reject(new Error("MFA_REQUIRED"));
        },
        mfaRequired: () => {
          pendingMfaUser = cognitoUser;
          pendingMfaType = "SMS_MFA";
          reject(new Error("MFA_REQUIRED"));
        },
      });
    });
  };

  /**
   * Complete new password challenge (first login)
   */
  const completeNewPassword = (
    email: string,
    oldPassword: string,
    newPassword: string,
  ): Promise<{user: User; tokens: CognitoTokens}> => {
    return new Promise((resolve, reject) => {
      const pool = getUserPool();
      const cognitoUser = new CognitoUser({Username: email, Pool: pool});
      cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");
      const authDetails = new AuthenticationDetails({Username: email, Password: oldPassword});

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session: CognitoUserSession) => {
          resolve({
            user: sessionToUser(session),
            tokens: {
              idToken: session.getIdToken().getJwtToken(),
              accessToken: session.getAccessToken().getJwtToken(),
            },
          });
        },
        onFailure: (err: Error) => {
          if (isStalePoolError(err)) {
            clearCognitoLocalStorage();
          }
          reject(err);
        },
        newPasswordRequired: (_userAttributes: Record<string, string>) => {
          cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
            onSuccess: (session: CognitoUserSession) => {
              resolve({
                user: sessionToUser(session),
                tokens: {
                  idToken: session.getIdToken().getJwtToken(),
                  accessToken: session.getAccessToken().getJwtToken(),
                },
              });
            },
            onFailure: (err: Error) => {
              reject(err);
            },
            totpRequired: () => {
              pendingMfaUser = cognitoUser;
              pendingMfaType = "SOFTWARE_TOKEN_MFA";
              reject(new Error("MFA_REQUIRED"));
            },
            mfaRequired: () => {
              pendingMfaUser = cognitoUser;
              pendingMfaType = "SMS_MFA";
              reject(new Error("MFA_REQUIRED"));
            },
          });
        },
      });
    });
  };

  /**
   * Refresh the current session tokens
   */
  const refreshSession = (): Promise<CognitoTokens | null> => {
    return new Promise((resolve) => {
      const pool = getUserPool();
      const cognitoUser = pool.getCurrentUser();

      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          resolve(null);
          return;
        }

        const refreshToken = session.getRefreshToken();
        cognitoUser.refreshSession(refreshToken, (refreshErr: Error | null, newSession: CognitoUserSession | null) => {
          if (refreshErr || !newSession) {
            resolve(null);
            return;
          }

          resolve({
            idToken: newSession.getIdToken().getJwtToken(),
            accessToken: newSession.getAccessToken().getJwtToken(),
          });
        });
      });
    });
  };

  /**
   * Submit the TOTP code for a pending MFA challenge (after signIn rejected with MFA_REQUIRED)
   */
  const submitMfaCode = (code: string): Promise<{user: User; tokens: CognitoTokens}> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = pendingMfaUser;
      if (!cognitoUser) {
        reject(new Error("MFA_SESSION_EXPIRED"));
        return;
      }

      cognitoUser.sendMFACode(code, {
        onSuccess: (session: CognitoUserSession) => {
          pendingMfaUser = null;
          resolve({
            user: sessionToUser(session),
            tokens: {
              idToken: session.getIdToken().getJwtToken(),
              accessToken: session.getAccessToken().getJwtToken(),
            },
          });
        },
        onFailure: (err: Error) => {
          reject(err);
        },
      }, pendingMfaType);
    });
  };

  const getAuthenticatedCognitoUser = (): Promise<CognitoUser> => {
    return new Promise((resolve, reject) => {
      const pool = getUserPool();
      const cognitoUser = pool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error("NOT_AUTHENTICATED"));
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          reject(err ?? new Error("NOT_AUTHENTICATED"));
          return;
        }
        resolve(cognitoUser);
      });
    });
  };

  /**
   * Whether the current user has a preferred TOTP factor (Cognito will challenge it at sign-in)
   */
  const getMfaStatus = async (): Promise<{totpConfigured: boolean}> => {
    const cognitoUser = await getAuthenticatedCognitoUser();
    return new Promise((resolve, reject) => {
      cognitoUser.getUserData((err, userData) => {
        if (err || !userData) {
          reject(err ?? new Error("MFA_STATUS_UNAVAILABLE"));
          return;
        }
        const settings = userData.UserMFASettingList ?? [];
        resolve({
          totpConfigured:
            settings.includes("SOFTWARE_TOKEN_MFA")
            && userData.PreferredMfaSetting === "SOFTWARE_TOKEN_MFA",
        });
      });
    });
  };

  /**
   * Start TOTP enrollment; resolves with the shared secret for QR/manual entry
   */
  const beginTotpSetup = async (): Promise<string> => {
    const cognitoUser = await getAuthenticatedCognitoUser();
    return new Promise((resolve, reject) => {
      cognitoUser.associateSoftwareToken({
        associateSecretCode: (secretCode: string) => resolve(secretCode),
        onFailure: (err: Error) => reject(err),
      });
    });
  };

  /**
   * Verify the first authenticator code and mark TOTP as the preferred MFA factor
   */
  const completeTotpSetup = async (code: string): Promise<void> => {
    const cognitoUser = await getAuthenticatedCognitoUser();
    return new Promise((resolve, reject) => {
      cognitoUser.verifySoftwareToken(code, "AIL Tutor", {
        onSuccess: () => {
          cognitoUser.setUserMfaPreference(null, {Enabled: true, PreferredMfa: true}, (err) => {
            if (err) {
              reject(err);
              return;
            }
            resolve();
          });
        },
        onFailure: (err: Error) => reject(err),
      });
    });
  };

  /**
   * Disable the TOTP factor; Cognito stops challenging MFA at sign-in
   */
  const disableTotp = async (): Promise<void> => {
    const cognitoUser = await getAuthenticatedCognitoUser();
    return new Promise((resolve, reject) => {
      cognitoUser.setUserMfaPreference(null, {Enabled: false, PreferredMfa: false}, (err) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  };

  /**
   * Sign out (clear local tokens)
   */
  const signOut = (): void => {
    pendingMfaUser = null;
    const pool = getUserPool();
    const cognitoUser = pool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
  };

  /**
   * Get current access token (for API calls), refreshing if needed
   */
  const getAccessToken = async (): Promise<string | null> => {
    const result = await getSession();
    if (result) {
      return result.tokens.accessToken;
    }

    // Try refresh
    const refreshed = await refreshSession();
    return refreshed?.accessToken ?? null;
  };

  const getIdToken = async (): Promise<string | null> => {
    const result = await getSession();
    if (result) {
      return result.tokens.idToken;
    }

    const refreshed = await refreshSession();
    return refreshed?.idToken ?? null;
  };

  return {
    getSession,
    signIn,
    completeNewPassword,
    submitMfaCode,
    getMfaStatus,
    beginTotpSetup,
    completeTotpSetup,
    disableTotp,
    refreshSession,
    signOut,
    getAccessToken,
    getIdToken,
  };
};
